// Status shown as a colored dot plus a plain-language label. Used for
// Run/Task/Approval/Credential statuses, health and risk throughout the app.

import { useIllustrations } from '../lib/illustrations'

export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

export interface StatusBadgeProps {
  /** The raw status/risk value (e.g. a RunStatus, RiskLevel or ApprovalStatus). */
  status: string
  /** Overrides the rendered text; defaults to the friendly label for `status`. */
  label?: string
}

// An unrecognized status still renders (as 'neutral'), it just isn't
// color-coded — this list is expected to grow as more enums get a UI.
const STATUS_TONE: Record<string, StatusTone> = {
  // RunStatus (FD §17.1)
  queued: 'neutral',
  provisioning: 'info',
  running: 'info',
  awaiting_approval: 'warning',
  verifying: 'info',
  completed: 'success',
  blocked: 'danger',
  failed: 'danger',
  cancelled: 'neutral',
  timed_out: 'danger',
  paused: 'warning',
  handed_off: 'neutral',
  // ApprovalStatus
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  expired: 'neutral',
  stale: 'neutral',
  // RiskLevel / Approval.riskIndicator
  low: 'neutral',
  medium: 'warning',
  high: 'danger',
  critical: 'danger',
  // TaskStatus
  validating: 'info',
  assigned: 'info',
  in_progress: 'info',
  // HealthStatus (executors, MCP services)
  unknown: 'neutral',
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'danger',
  // CredentialStatus / Skill approval
  active: 'success',
  inactive: 'neutral',
  revoked: 'neutral',
  error: 'danger',
  pending_review: 'warning',
}

/** Labels that read better than the humanized enum value. */
const STATUS_LABEL: Record<string, string> = {
  awaiting_approval: 'Waiting for you',
  timed_out: 'Timed out',
  handed_off: 'Handed off',
  pending_review: 'Needs review',
  low: 'Low risk',
  medium: 'Medium risk',
  high: 'High risk',
  critical: 'Critical risk',
  pending: 'Pending',
}

const DOT: Record<StatusTone, string> = {
  neutral: 'bg-ink-3',
  info: 'bg-info',
  success: 'bg-ok',
  warning: 'bg-signal',
  danger: 'bg-danger',
}

const TEXT: Record<StatusTone, string> = {
  neutral: 'text-ink-2',
  info: 'text-ink',
  success: 'text-ink',
  warning: 'text-signal',
  danger: 'text-danger',
}

export function statusTone(status: string): StatusTone {
  return STATUS_TONE[status] ?? 'neutral'
}

export function statusLabel(status: string): string {
  if (STATUS_LABEL[status]) return STATUS_LABEL[status]
  const text = status.replace(/_/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Risk levels get a tiny goblin face instead of a dot (illustrations on). */
const RISK_LEVELS = new Set(['low', 'medium', 'high', 'critical'])

export function StatusBadge({ status, label }: StatusBadgeProps) {
  const [illustrations] = useIllustrations()
  const tone = statusTone(status)
  const live =
    status === 'running' || status === 'provisioning' || status === 'verifying'
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap text-base font-medium ${TEXT[tone]}`}
      data-status={status}
    >
      {illustrations && RISK_LEVELS.has(status) ? (
        <img
          src={`/goblins/risk-${status}.webp`}
          alt=""
          aria-hidden
          width={18}
          height={18}
          className="h-[18px] w-[18px] shrink-0 select-none object-contain"
        />
      ) : (
        <span
          aria-hidden
          className={`h-2 w-2 shrink-0 rounded-full ${DOT[tone]} ${live ? 'motion-safe:animate-[track-pulse_1.6s_ease-in-out_infinite]' : ''}`}
        />
      )}
      {label ?? statusLabel(status)}
    </span>
  )
}
